import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { captureException } from '@sentry/react'
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  SendRoomMessageBody,
  chatConversationsQueryKey,
  chatRoomQueries,
  isRoomAccessError,
  sendRoomMessage,
} from '../../../api/chat'
import { societyQueries } from '../../../api/societies'
import { ConversationsResponse } from '../../../generated/api/chat-conversations'
import { UserChip } from '../../../generated/api/chat-room'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useAuth } from '../../../hooks/useAuth'
import { useHamburger } from '../../../hooks/useHamburgerContext'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { useSelectedChatId } from '../../../state/context/SelectedChatIdProvider'
import { ToastType } from '../../common/Toast/toast-type'
import { Composer } from './Composer'
import { JoinBar } from './JoinBar'
import { MessageList } from './MessageList'
import { RoomHeader } from './RoomHeader'
import { RoomSettingsPanel, hasRoomSettings } from './Settings'
import { useMarkRoomRead } from './useMarkRoomRead'
import { useRoomMessagesCache } from './useRoomMessagesCache'

interface RoomPaneProps {
  roomId: string
}

export const RoomPane = ({ roomId }: RoomPaneProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { userId } = useAuth()
  const { isHamburgerMode } = useHamburger()
  const navigate = useNavigate()
  const client = useQueryClient()
  const { setSelectedChatRoomId } = useSelectedChatId()
  const { insert, removeRoom } = useRoomMessagesCache()
  const [showSettings, setShowSettings] = useState(false)
  const accessLossHandled = useRef<string | null>(null)

  const headerQuery = useQuery(chatRoomQueries.header(roomId))
  const messagesQuery = useInfiniteQuery(chatRoomQueries.messages(roomId))
  const readsQuery = useQuery(chatRoomQueries.reads(roomId))

  const header = headerQuery.data
  const cachedIdentity = useMemo(() => {
    const row = client
      .getQueriesData<InfiniteData<ConversationsResponse>>({
        queryKey: chatConversationsQueryKey,
      })
      .flatMap(([, data]) => data?.pages ?? [])
      .flatMap((page) => page.items)
      .find((item) => item.id === roomId)
    if (!row) return undefined
    const societyName = client
      .getQueryData(societyQueries.getUserSocieties(userId).queryKey)
      ?.find((society) => society._id === row.societyId)?.name
    return { ...row, societyName }
  }, [client, roomId, userId])
  const messages = messagesQuery.data?.pages.flatMap((page) => page.items) ?? []
  const latestMessageId = messagesQuery.data?.pages[0]?.items[0]?.id

  const readersByMessage = useMemo(() => {
    const map = new Map<string, UserChip[]>()
    readsQuery.data?.forEach(({ messageId, ...chip }) => {
      if (chip.userId === userId) return
      map.set(messageId, [...(map.get(messageId) ?? []), chip])
    })
    return map
  }, [readsQuery.data, userId])

  // A previewer isn't a participant, and the api refuses its read.
  const canMarkRead = header?.capabilities.canJoin === false
  useMarkRoomRead(roomId, canMarkRead ? latestMessageId : undefined)

  useEffect(() => {
    setShowSettings(false)
  }, [roomId])

  useEffect(() => {
    const lostAccess =
      isRoomAccessError(headerQuery.error) ||
      isRoomAccessError(messagesQuery.error)
    if (!lostAccess || accessLossHandled.current === roomId) return

    accessLossHandled.current = roomId
    removeRoom(roomId)
    client.invalidateQueries({ queryKey: chatConversationsQueryKey })
    setSelectedChatRoomId(undefined)
    setToastNotification(
      ToastType.DANGER,
      translate('chatRoom.flashMessage.noAccess')
    )
    navigate(reverseUrl('chat'))
  }, [
    client,
    headerQuery.error,
    messagesQuery.error,
    navigate,
    removeRoom,
    roomId,
    setSelectedChatRoomId,
    setToastNotification,
    translate,
  ])

  const { mutateAsync: send, isPending: isSending } = useMutation({
    mutationFn: async (body: SendRoomMessageBody) =>
      (await sendRoomMessage(roomId, body)).data.data,
    meta: { skipGlobalInvalidate: true },
    onSuccess: (row) => {
      insert(row)
      client.invalidateQueries({ queryKey: chatConversationsQueryKey })
    },
    onError: (error) => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
      captureException(error)
    },
  })

  const showJoinBar = header?.capabilities.canJoin ?? false

  return (
    <div className={`flex flex-1 ${isHamburgerMode ? 'flex-col' : ''}`}>
      <div className="flex min-h-0 flex-1 flex-col justify-between">
        <RoomHeader
          identity={header ?? cachedIdentity}
          capabilities={header?.capabilities}
          onToggleSettings={() => setShowSettings((value) => !value)}
        />
        <MessageList
          roomId={roomId}
          header={header}
          messages={messages}
          readersByMessage={readersByMessage}
          currentUserId={userId}
          isLoading={messagesQuery.isLoading}
          hasNextPage={messagesQuery.hasNextPage}
          isFetchingNextPage={messagesQuery.isFetchingNextPage}
          fetchNextPage={messagesQuery.fetchNextPage}
        />
        {header &&
          (showJoinBar ? (
            <JoinBar roomId={roomId} />
          ) : (
            <Composer
              key={roomId}
              roomId={roomId}
              societyId={header.societyId}
              canSend={header.capabilities.canSend}
              sendBlockedReason={header.sendBlockedReason}
              isSending={isSending}
              onSend={send}
            />
          ))}
      </div>
      {showSettings && header && hasRoomSettings(header.capabilities) && (
        <RoomSettingsPanel header={header} />
      )}
    </div>
  )
}
